import nextId from 'react-id-generator';
import { brandsOpenApi, COLORS, defaultTimeProps, defaultTimezoneOffset } from '@constants';
import { axiosBoa } from '@utils';
import { getTimezone } from '@utils/getTimezone';
import dayjs from 'dayjs';
import * as fileDownload from 'js-file-download';
import { getParent, types } from 'mobx-state-tree';
import { graphColorsMap } from '../pages/Performance/components/Overview/constants';
import { DETAILIZATION_TAB_KEYS, METRICS, PERFORMANCE_METRICS } from '../pages/Performance/constants';
import { detailizationByKey } from '../pages/Performance/utils';
import { models } from './constants';
import { createStoreAction } from './utils';

export const PerformanceStore = types
    .model('PerformanceStore', {
        performanceList: types.array(models.PerformanceModel),
        reportsList: types.array(models.ReportsModel),
        metrics: types.array(models.MetricsModel),
        detailizationTab: types.maybeNull(types.string),
        selectedChartMetrics: types.array(types.string),
        chartLabels: types.array(types.string),
        chartDatasets: types.frozen(),
        performanceCampaigns: types.array(
            types.model({
                id: types.maybeNull(types.number),
                title: types.maybeNull(types.string)
            })
        ),
        liveCampaigns: types.array(
            types.model({
                revenue: types.maybeNull(types.number),
                roas: types.maybeNull(types.string),
                id: types.maybeNull(types.number),
                title: types.maybeNull(types.string),
                priority: types.maybeNull(types.number),
                account: types.model({
                    shortId: types.maybeNull(types.string)
                }),
                views: types.maybeNull(types.number),
                spend: types.maybeNull(types.number)
            })
        ),
        selectedReportMetrics: types.array(
            types.model({
                title: types.maybeNull(types.string),
                dataIndex: types.maybeNull(types.string),
                key: types.maybeNull(types.string),
                width: types.maybeNull(types.string),
                category: types.maybeNull(types.string),
                tooltip: types.maybeNull(types.string),
                active: types.maybeNull(types.boolean),
                buffer: types.maybeNull(types.boolean),
                default: types.maybeNull(types.boolean)
            })
        ),
        shopDomain: types.maybeNull(types.string),
        overviewPending: true,
        pendingLiveCampaigns: false,
        pendingMetricsChart: false,
        pendingMetricsOverview: false,
        pendingReports: true,
        pendingBreakdowns: true
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        },
        get liveCampaignsList() {
            return this.liveCampaigns.slice(0, 3);
        },
        get activeReportMetrics() {
            return this.selectedReportMetrics.filter((item) => item.active);
        },
        get timezoneOffset() {
            const currentAccount = self.root.accountsStore?.currentAccount;

            if (!currentAccount) {
                return defaultTimezoneOffset;
            }

            const timeZone = getTimezone({ prefix: null, currentAccount: currentAccount });
            return timeZone ?? defaultTimezoneOffset;
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            setDefaultReportMetrics: () => {
                self.selectedReportMetrics = Object.values(METRICS);
            },
            setBufferReportMetrics: (checked, item) => {
                const foundIndex = self.selectedReportMetrics.findIndex((x) => x.key === item.key);
                self.selectedReportMetrics[foundIndex].buffer = checked;
            },
            resetBufferReportMetric: () => {
                self.selectedReportMetrics = self.selectedReportMetrics.map((column) => {
                    return { ...column, buffer: null };
                });
            },
            applyReportMetric: () => {
                self.selectedReportMetrics = self.selectedReportMetrics.map((column) => {
                    return !Object.is(column?.buffer, null) && !Object.is(column?.buffer, undefined)
                        ? { ...column, active: column.buffer, buffer: null }
                        : column;
                });
            },
            setDetailizationTab: (tab) => {
                self.detailizationTab = tab ?? DETAILIZATION_TAB_KEYS.DAYS;
            },
            addMetricChart: (label) => {
                self.selectedChartMetrics = [...self.selectedChartMetrics, label];
            },
            deleteMetricChart: (label) => {
                if (self.selectedChartMetrics.length > 1) {
                    self.chartDatasets = self.chartDatasets.filter((item) => item.label !== label);
                    self.selectedChartMetrics = self.selectedChartMetrics.filter((item) => item !== label);
                }
            },
            getOverview: action(
                function* getOverview(props = {}) {
                    self.overviewPending = true;

                    yield self.getLiveCampaigns(props);
                    yield self.getChartData(props);
                    yield self.getMetricsOverview(props);
                },
                { onFinally: () => (self.overviewPending = false) }
            ),
            getCampaignsForPerformance: action(function* getCampaignsForPerformance() {
                const response = yield axiosBoa.get(brandsOpenApi.performance.campaigns.list, {
                    params: {
                        pagination: 'none'
                    }
                });

                self.performanceCampaigns = response.data;
            }),
            getLiveCampaigns: action(
                function* getLiveCampaigns(props = {}) {
                    self.pendingLiveCampaigns = true;
                    const params = {
                        timezone_offset: self.timezoneOffset,
                        date_from: props.date_from,
                        date_to: props.date_to
                    };

                    self.liveCampaigns = yield axiosBoa.get(brandsOpenApi.performance.liveCampaigns.get, { params });
                },
                { onFinally: () => (self.pendingLiveCampaigns = false) }
            ),
            getMetricsOverview: action(
                function* getMetricsOverview(props = {}) {
                    self.pendingMetricsOverview = true;

                    const params = {
                        timezone_offset: self.timezoneOffset,
                        ...props
                    };
                    self.performanceList = yield axiosBoa.get(brandsOpenApi.performance.metricsOverview.get, {
                        params
                    });
                },
                { onFinally: () => (self.pendingMetricsOverview = false) }
            ),
            getShopifyAccountPerformance: action(
                function* getShopifyAccountPerformance() {
                    const defaultProps = {
                        ...defaultTimeProps
                    };

                    const response = yield axiosBoa.post(brandsOpenApi.auth.embedded, {
                        ...self.root.authStore.embeddedLoadPayload,
                        ...self.root.authStore.oauthPayload,
                        performance: {
                            ...defaultProps,
                            metrics: PERFORMANCE_METRICS
                        }
                    });
                    self.performanceList = response.list;
                    self.shopDomain = response.shopDomain;
                },
                { onFinally: () => (self.overviewPending = false) }
            ),
            getMetricChart: action(function* getMetricChart(metric, params) {
                const result = yield axiosBoa.get(brandsOpenApi.performance.metricsChart.get(metric), { params });

                const labels = result.labels.map((value) => {
                    return self.detailizationTab === DETAILIZATION_TAB_KEYS.MONTHS
                        ? dayjs(value.replace('/', '/01/')).format('MM/YYYY')
                        : dayjs(value).format('D');
                });

                const metricColorId = METRICS[result.datasets[0].label].color;
                const datasets = [
                    {
                        ...result.datasets[0],
                        borderColor: graphColorsMap[metricColorId],
                        backgroundColor: COLORS.$white,
                        dates: result.labels.map((value) => {
                            return self.detailizationTab === DETAILIZATION_TAB_KEYS.MONTHS
                                ? dayjs(value.replace('/', '/01/')).format('MMM YYYY')
                                : dayjs(value).format('D MMM YYYY');
                        })
                    }
                ];

                return { labels, datasets };
            }),
            getChartData: action(
                function* getChartData(props = {}) {
                    self.pendingMetricsChart = true;

                    const params = {
                        timezone_offset: self.timezoneOffset,
                        date_trunc: detailizationByKey(self.detailizationTab),
                        ...props
                    };

                    if (!props.selectedMetric && self.selectedChartMetrics.length > 1) {
                        const datasets = [];
                        let labels;

                        for (const metric of self.selectedChartMetrics) {
                            const { labels: metricLabels, datasets: metricDatasets } = yield self.getMetricChart(
                                metric,
                                params
                            );
                            labels = metricLabels;
                            datasets.push(...metricDatasets);
                        }

                        self.chartDatasets = datasets;
                        self.chartLabels = labels;
                    } else {
                        const metric = props.selectedMetric || self.selectedChartMetrics[0];
                        const { labels: metricLabels, datasets: metricDatasets } = yield self.getMetricChart(
                            metric,
                            params
                        );

                        self.chartDatasets = [
                            ...self.chartDatasets.filter((item) => item.label !== metricDatasets[0].label),
                            ...metricDatasets
                        ];

                        if (JSON.stringify(self.chartLabels) !== JSON.stringify(metricLabels)) {
                            self.chartLabels = metricLabels;
                        }
                    }
                },
                { onFinally: () => (self.pendingMetricsChart = false) }
            ),
            downloadChartData: action(function* downloadChartData(props) {
                const params = {
                    download: true,
                    timezone_offset: self.timezoneOffset,
                    date_trunc: detailizationByKey(self.detailizationTab),
                    metrics: self.selectedChartMetrics,
                    ...props
                };
                const data = yield axiosBoa.get(
                    brandsOpenApi.performance.metricsChart.get(self.selectedChartMetrics[0]),
                    { params }
                );

                const from = dayjs(params.date_from).format('DD/MM/YYYY');
                const to = dayjs(params.date_to).format('DD/MM/YYYY');
                fileDownload(data, `performance_overview_${from}-${to}.csv`);
            }),
            getBreakdowns: action(
                function* getBreakdowns(props = {}) {
                    self.pendingBreakdowns = true;

                    const params = {
                        timezone_offset: self.timezoneOffset,
                        ...props
                    };
                    self.breakdowns = yield axiosBoa.get(brandsOpenApi.performance.breakdowns.get, { params });
                },
                { onFinally: () => (self.pendingBreakdowns = false) }
            ),
            getTopSites: action(
                function* getTopSites(props = {}) {
                    self.pendingTopSites = true;

                    const params = {
                        timezone_offset: self.timezoneOffset,
                        ...props
                    };
                    self.topSites = yield axiosBoa.get(brandsOpenApi.performance.topSites.get, { params });
                },
                { onFinally: () => (self.pendingTopSites = false) }
            ),
            getReports: action(
                function* getReports(props = {}) {
                    self.pendingReports = true;

                    const params = {
                        timezone_offset: self.timezoneOffset,
                        date_from: props.date_from,
                        date_to: props.date_to,
                        metrics: self.selectedReportMetrics
                            .filter((item) => item.active && item.key !== 'campaign')
                            .map((item) => item.key)
                    };

                    const reportsList = yield axiosBoa.get(brandsOpenApi.performance.reports.get, { params });

                    self.reportsList = reportsList
                        .map((report) => ({
                            ...report,
                            key: nextId()
                        }))
                        .sort((a, b) => {
                            // Handle case when revenue is "-", "$396", etc.
                            const parseValue = (val) => {
                                if (val === '-') return 0;
                                // Remove currency symbols and commas, then convert to number
                                return Number(String(val).replace(/[$,]/g, '')) || 0;
                            };
                            const revenueA = parseValue(a.revenue);
                            const revenueB = parseValue(b.revenue);
                            return revenueB - revenueA; // Sort in descending order
                        });
                },
                { onFinally: () => (self.pendingReports = false) }
            ),
            downloadReports: action(function* downloadReports(props = {}) {
                const params = {
                    timezone_offset: self.timezoneOffset,
                    metrics: self.selectedReportMetrics.filter((item) => item.active).map((item) => item.key),
                    download: true,
                    ...props
                };

                const data = yield axiosBoa.get(brandsOpenApi.performance.reports.get, { params });

                const from = dayjs(params.date_from).format('DD/MM/YYYY');
                const to = dayjs(params.date_to).format('DD/MM/YYYY');
                fileDownload(data, `performance_report_${from}-${to}.csv`);
            }),
            afterCreate: () => {
                self.detailizationTab = DETAILIZATION_TAB_KEYS.DAYS;
                self.selectedReportMetrics = Object.values(METRICS);
                self.selectedChartMetrics = [PERFORMANCE_METRICS[0]];
                self.chartDatasets = [];
                self.chartLabels = [];
            }
        };
    });
